import React from 'react';
import { find, isEmpty } from 'lodash';
import Link from 'components/link';
import { singularize, wordify } from 'utils';
import { removeModelNamespace } from 'utils/content';

export const MENU_ITEM_OPTIONS = [
  { value: 'home', label: 'Home page', hint: 'Link to the home page.' },
  {
    value: 'search',
    label: 'Search',
    hint: 'Link to a search page with an optional pre-defined query.',
    description: ({ options }) => (
      <>Search {options && options.query ? `for "${options.query}"` : 'page'}</>
    ),
  },
  {
    value: 'product_list',
    label: 'All products',
    hint: 'Link to a page listing all products.',
  },
  {
    value: 'product',
    label: 'Product',
    hint: 'Link to a product detail page.',
    requiresValue: true,
    description: ({ value }, linkValue) => (
      <>
        <Link to={`/products/${value.id || value}`}>{linkValue}</Link> (product)
      </>
    ),
  },
  {
    value: 'category',
    label: 'Category',
    hint: 'Link to a category of products.',
    requiresValue: true,
    description: ({ value }, linkValue) => (
      <>
        <Link to={`/categories/${value.id || value}`}>{linkValue}</Link>{' '}
        (category)
      </>
    ),
  },
  {
    value: 'page',
    label: 'Page',
    hint: 'Link to a content page.',
    requiresValue: true,
    description: ({ value }, linkValue) => (
      <>
        <Link to={`/content/pages/${value.id || value}`}>{linkValue}</Link>{' '}
        (page)
      </>
    ),
  },
  {
    value: 'blog',
    label: 'Blog post',
    hint: 'Link to a blog post.',
    requiresValue: true,
    description: ({ value }, linkValue) => (
      <>
        <Link to={`/content/blogs:posts/${value.id || value}`}>
          {linkValue}
        </Link>{' '}
        (blog)
      </>
    ),
  },
  {
    value: 'blog_category',
    label: 'Blog category',
    hint: 'Link to a page listing all blogs in a category.',
    requiresValue: true,
    description: ({ value }, linkValue) => (
      <>
        <Link to={`/content/blog-categories/${value.id || value}`}>
          {linkValue}
        </Link>{' '}
        (blog category)
      </>
    ),
  },
  {
    value: 'content_list',
    label: 'Content list',
    hint: 'Link to a page listing all content entries.',
    description: ({ model }) => (
      <>
        <Link to={`/collections/${model.replace('/', '_')}`}>
          {wordify(singularize(removeModelNamespace(model))).toLowerCase() ||
            'content'}{' '}
          list
        </Link>
      </>
    ),
  },
  {
    value: 'content',
    label: 'Content entry',
    hint: 'Link to a content entry.',
    requiresValue: true,
    description: ({ value, model }, linkValue) => (
      <>
        <Link
          to={`/collections/${model.replace('/', '_')}/${value.id || value}`}
        >
          {linkValue}
        </Link>{' '}
        (
        {wordify(singularize(removeModelNamespace(model))).toLowerCase() ||
          'content'}
        )
      </>
    ),
  },
  {
    value: 'url',
    label: 'URL',
    hint: 'Link to a specified URL.',
    requiresValue: true,
    description: ({ value, options }) => (
      <>
        Link to {value} {options && options.target && `(new tab)`}
      </>
    ),
  },
  {
    value: 'heading',
    label: 'Heading',
    hint: 'Display a heading above a set of menu items.',
  },
];

export function renderMenuItemLinkDescription(item) {
  const { type, value } = item;
  const itemOption =
    find(MENU_ITEM_OPTIONS, { value: type }) ||
    find(MENU_ITEM_OPTIONS_DEPRECATED, { value: type });

  if (!itemOption) {
    return null;
  }

  const { label, description, requiresValue } = itemOption;

  const linkValue = value?.title ?? value?.name ?? value?.id ?? value;
  if (itemOption && isEmpty(linkValue) && requiresValue) {
    return null;
  }

  if (typeof description === 'function') {
    return description(item, linkValue);
  }

  return description || label;
}

export function renderMenuItemOptionHint(itemValues) {
  const { type } = itemValues;
  const itemOption =
    find(MENU_ITEM_OPTIONS, { value: type }) ||
    find(MENU_ITEM_OPTIONS_DEPRECATED, { value: type });

  if (!itemOption) {
    return null;
  }

  const { hint } = itemOption;

  if (typeof hint === 'function') {
    return description(itemValues);
  }

  return hint;
}

export function isTypeValue(type, expected) {
  return (
    type &&
    (type === expected || type === MENU_ITEM_DEPRECATED_OPTION_MAP[expected])
  );
}

export function typeLabel(type) {
  return (
    find(MENU_ITEM_OPTIONS, { value: type })?.label ||
    find(MENU_ITEM_OPTIONS_DEPRECATED, { value: type })?.label ||
    undefined
  );
}

/**
 * Deprecated item options
 * Only 1 client uses these most likely as of 3/2024: PepperSQ
 */
export const MENU_ITEM_OPTIONS_DEPRECATED = [
  { value: 'product_all', label: 'All products' }, // special
  { value: 'category_all', label: 'All categories' }, // special
  { value: 'product', label: 'Product' },
  { value: 'category', label: 'Category' },
  { value: 'search', label: 'Search' },
  { value: 'home', label: 'Home' },
  { value: 'link', label: 'Link' },
];

export const MENU_ITEM_DEPRECATED_OPTION_MAP = {
  url: 'link',
};

export const MENU_ITEM_DEPRECATED_CLIENTS = ['peppersq', 'peppersq-dev'];

export function isExcludedClient(client) {
  return MENU_ITEM_DEPRECATED_CLIENTS.indexOf(client.id) !== -1;
}
